import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PortalAuthShell from '../../components/PortalAuthShell';

export default function PortalAcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/portal/invite/${token}`);
        const data = await res.json();
        if (!res.ok) { setError(data.detail || 'Invite invalid.'); return; }
        setInvite(data);
      } catch (e) { setError('Could not load invite.'); }
    };
    load();
  }, [token, backendUrl]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { alert('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { alert('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${backendUrl}/api/portal/invite/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      localStorage.setItem('portal_token', data.access_token);
      localStorage.setItem('portal_client_id', data.client.id);
      localStorage.setItem('portal_client_name', data.client.name);
      localStorage.setItem('portal_client_email', data.client.email);
      navigate('/portal/dashboard');
    } catch (e) { alert(`Could not accept invite: ${e.message}`); }
    finally { setSubmitting(false); }
  };

  return (
    <PortalAuthShell>
      {error ? (
        <div className="text-center">
          <div className="h-16 w-16 mx-auto rounded-full bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-red-500"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mt-4">Invite unavailable</h2>
          <p className="text-gray-600 mt-2">{error}</p>
          <Link to="/portal/login" className="inline-block mt-6 px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium">Go to login</Link>
        </div>
      ) : !invite ? (
        <div className="text-center text-gray-500 py-16">Loading…</div>
      ) : (
        <>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-xs font-semibold">
            <span className="h-1.5 w-1.5 rounded-full bg-teal-500"></span> You’re invited
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mt-3">Welcome, {invite.name.split(' ')[0]}</h2>
          <p className="text-gray-600 mt-2">Set a password to activate your Client Portal account.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input type="email" value={invite.email} disabled className="w-full pl-10 pr-4 py-3 border border-gray-200 bg-gray-50 rounded-xl text-gray-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Choose a password *</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none" data-testid="invite-password-input" />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">Min 8 characters. Use a mix of letters, numbers &amp; symbols.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm password *</label>
              <div className="relative">
                <i className="ri-lock-2-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none" data-testid="invite-confirm-input" />
              </div>
            </div>
            <button type="submit" disabled={submitting} data-testid="invite-accept-btn" className="w-full text-white py-3 rounded-xl font-semibold text-[15px] transition disabled:opacity-50 shadow-lg shadow-teal-500/20 hover:shadow-teal-500/40" style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)' }}>
              {submitting ? 'Activating…' : 'Activate my account'}
            </button>
          </form>
        </>
      )}
    </PortalAuthShell>
  );
}
